import { useSrpApiContext } from "@/providers/SRP/provider";
import type { SrpRequestParams } from "@/providers/SRP/types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ZodSchema } from "zod";

/**
 * Custom hook to fetch part data with optional schema validation
 *
 * @template T - The expected return type
 * @param {string} pathname - The API endpoint pathname
 * @param {boolean} enabled - Flag to enable/disable the query
 * @param {SrpRequestParams} params - Request parameters
 * @param {ZodSchema<T>} [schema] - Optional Zod schema for response validation
 * @param {string} [baseURL] - Optional base URL for the API request
 * @returns {UseQueryResult<T>} Query result containing the fetched data
 * @throws {Error} When used outside of SrpApiContext
 */
export default function useSRP<T>(
	pathname: string,
	enabled: boolean,
	params: SrpRequestParams,
	schema?: ZodSchema<T>,
	baseURL?: string,
) {
	const context = useSrpApiContext();
	if (!context) {
		throw new Error("SrpApiContext not found");
	}
	const { getFn } = context;
	return useQuery<T>({
		throwOnError: true,
		enabled,
		queryKey: [pathname, params, baseURL],
		refetchInterval: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
		queryFn: async () => {
			const json = await getFn({ pathname, params, baseURL });
			return schema ? (schema.parse(json) as T) : (json as T);
		},
		placeholderData: keepPreviousData,
	});
}
